<template>
  <div class="col-12">
    <div class="card mb-5" style="margin-top: 4rem; overflow: visible">
      <div class="card-header p-0 position-relative mt-n4 mx-3 z-index-2" style="height: 60px;">
        <div class="bg-primary shadow-primary border-radius-lg pt-4 pb-3 h-100">
          <div class="d-flex justify-content-between align-items-center">
            <h6 class="text-white text-capitalize ps-3 pe-3 mb-0">{{ t('class_fees.edit_fee_title') }}</h6>
          </div>
        </div>
      </div>
      
      <div class="card-body px-0 pb-2 pe-3">
        <div class="row justify-content-center">
          <div class="col-md-8 ms-3">
            <LoadingSpinner v-if="isLoading" :message="t('class_fees.loading_fees')" />
            
            <form v-else @submit.prevent="submitForm" @keydown.enter.prevent>
              <div class="row g-3">
                <div class="col-md-6">
                  <label class="bmd-label-float">{{ t('class_fees.class') }}</label>
                  <select 
                    v-model="form.class_id" 
                    class="form-select borderless-input"
                    :class="{ 'is-invalid': errors.class_id }"
                    required
                    @blur="validateField('class_id')"
                    :disabled="isUpdating"
                  >
                    <option value="" disabled>{{ t('class_fees.select_class') }}</option>
                    <option v-for="classItem in classes" :value="classItem.id" :key="classItem.id">
                      {{ classItem.name }}
                    </option>
                  </select>
                  <div class="invalid-feedback">{{ errors.class_id }}</div>
                </div>
                
                <div class="col-md-6">
                  <label class="bmd-label-float">{{ t('class_fees.academic_year') }}</label>
                  <select 
                    v-model="form.academic_year_id" 
                    class="form-select borderless-input"
                    :class="{ 'is-invalid': errors.academic_year_id }"
                    required
                    @blur="validateField('academic_year_id')"
                    :disabled="isUpdating"
                  >
                    <option value="" disabled>{{ t('class_fees.select_academic_year') }}</option>
                    <option v-for="year in academicYears" :value="year.id" :key="year.id">
                      {{ year.name }} ({{ year.year }})
                    </option>
                  </select>
                  <div class="invalid-feedback">{{ errors.academic_year_id }}</div>
                </div>
                
                <div class="col-md-6">
                  <label class="bmd-label-float">{{ t('class_fees.amount') }}</label>
                  <div class="input-group">
                    <span class="input-group-text">{{ t('class_fees.lyd_abbr') }}</span>
                    <input 
                      type="number" 
                      v-model="form.amount" 
                      class="form-control borderless-input"
                      :class="{ 'is-invalid': errors.amount }"
                      required 
                      min="0" 
                      step="0.01"
                      @blur="validateField('amount')"
                      :disabled="isUpdating"
                    >
                  </div>
                  <div class="invalid-feedback">{{ errors.amount }}</div>
                </div>
                
          
                <div class="col-12 mt-4">
                  <div class="d-flex gap-3">
                    <button 
                      type="submit" 
                      class="btn btn-primary flex-grow-1"
                      :disabled="isUpdating || !hasChanges"
                    >
                      <span v-if="isUpdating">
                        <i class="fas fa-spinner fa-spin me-2"></i>
                        {{ t('class_fees.saving') }}
                      </span>
                      <span v-else>
                        <i class="fas fa-save ms-2"></i>
                        {{ t('class_fees.save_edits') }}
                      </span>
                    </button>
                    <router-link 
                      :to="{ name: 'class-fees.index' }" 
                      class="btn btn-outline-secondary flex-grow-1"
                      :disabled="isUpdating"
                    >
                      <i class="fas fa-times ms-2"></i>
                      {{ t('class_fees.cancel') }}
                    </router-link>
                  </div>
                </div>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useToast } from 'vue-toastification';
import { useI18n } from 'vue-i18n';
import LoadingSpinner from "@/components/common/LoadingSpinner.vue";
import api from '@/api';

const { t, locale } = useI18n();
const toast = useToast();
const route = useRoute();
const router = useRouter();

// Backend business-rule errors (e.g. "can't edit, already in use") come back
// as { message: { en, ar } } rather than a single localized string.
const extractApiMessage = (error, fallbackKey = 'class_fees.save_failed') => {
  const data = error?.response?.data;
  if (data?.message) {
    if (typeof data.message === 'object' && data.message !== null) {
      const loc = (locale.value || 'ar').startsWith('ar') ? 'ar' : 'en';
      if (typeof data.message[loc] === 'string') return data.message[loc];
    }
    if (typeof data.message === 'string') return data.message;
  }
  return t(fallbackKey);
};

const isLoading = ref(false);
const isUpdating = ref(false);
const classes = ref([]);
const academicYears = ref([]);
const form = ref({
  id: null,
  class_id: '',
  academic_year_id: '',
  amount: 0,
  description: ''
});
const originalFormData = ref(null);
const errors = ref({});

const hasChanges = computed(() => {
  if (!originalFormData.value) return false;
  
  return Object.keys(form.value).some(key => {
    if (key === 'id') return false;
    const currentValue = form.value[key];
    const originalValue = originalFormData.value[key];
    
    if (typeof currentValue === 'number') {
      return Number(currentValue) !== Number(originalValue);
    }
    
    return String(currentValue) !== String(originalValue);
  });
});

const fetchClasses = async () => {
  try {
    const response = await api.get('/classes', { params: { all: true } });
    classes.value = response.data.data;
  } catch (error) {
    console.error('Error loading classes:', error);
    toast.error(t('class_fees.load_classes_failed'));
  }
};

const fetchAcademicYears = async () => {
  try {
    const response = await api.get('/academic-years');
    academicYears.value = response.data.data;
  } catch (error) {
    console.error('Error loading academic years:', error);
    toast.error(t('class_fees.load_years_failed'));
  }
};

const fetchClassFee = async () => {
  isLoading.value = true;
  try {
    const response = await api.get(`/payments/class-fees/${route.params.id}`);
    const feeData = response.data.data;
    
    form.value = {
      id: feeData.id,
      class_id: feeData.class_id,
      academic_year_id: feeData.academic_year_id,
      amount: parseFloat(feeData.amount),
      description: feeData.description || ''
    };
    
    originalFormData.value = { ...form.value };
  } catch (error) {
    console.error('Error loading class fee:', error);
    toast.error(error.response?.data?.message || t('class_fees.load_fees_failed'));
    router.push({ name: 'class-fees.index' });
  } finally {
    isLoading.value = false;
  }
};

const validateField = (field) => {
  if (!form.value[field] && field !== 'description') {
    errors.value[field] = t('validation.required');
    return false;
  }
  
  if (field === 'amount' && form.value.amount < 0) {
    errors.value.amount = t('validation.amount_positive');
    return false;
  }
  
  delete errors.value[field];
  return true;
};

const validateForm = () => {
  let isValid = true;
  const requiredFields = ['class_id', 'academic_year_id', 'amount'];
  
  requiredFields.forEach(field => {
    if (!validateField(field)) {
      isValid = false;
    }
  });
  
  return isValid;
};

const submitForm = async () => {
  if (!validateForm()) {
    toast.warning(t('validation.please_correct_errors'));
    return;
  }

  if (!hasChanges.value) {
    toast.info(t('class_fees.no_changes'));
    return;
  }

  isUpdating.value = true;
  try {
    const submitData = {
      class_id: form.value.class_id,
      academic_year_id: form.value.academic_year_id,
      amount: parseFloat(form.value.amount),
      description: form.value.description,
    };

    await api.put(`/payments/class-fees/${route.params.id}`, submitData);
    toast.success(t('class_fees.update_success'));
    router.push({ name: 'class-fees.index' });
  } catch (error) {
    console.error('Error updating class fee:', error);

    if (error.response?.status === 422 && error.response.data.errors) {
      errors.value = error.response.data.errors;
      Object.values(error.response.data.errors).flat().forEach(message => {
        toast.error(message);
      });
    } else {
      toast.error(extractApiMessage(error));
    }
  } finally {
    isUpdating.value = false;
  }
};

onMounted(async () => {
  await Promise.all([
    fetchClasses(),
    fetchAcademicYears(),
    fetchClassFee()
  ]);
});
</script>